/** @fileoverview Checks tenant Fabrik access and billing plan status. */

import { useEffect, useState } from 'react';
import { getFabrikUsage } from '../services/billingApi';
import type { BillingUsage } from '../types/billing';
import type { Tenant } from '../types/identity';

export type BillingStatus = 'idle' | 'loading' | 'allowed' | 'blocked' | 'error';

export const hasFabrikAccess = (tenant?: Tenant) =>
  Boolean(
    tenant?.permissions?.some((permission) => permission.permission_code === 'access_fabrik')
  );

function hasActiveFabrikPlan(plan: BillingUsage | null) {
  const accessFeature = plan?.features?.find(
    (feature) => feature.feature_code === 'ENABLE_FABRIK_ACCESS'
  );
  const now = Date.now();
  return Boolean(
    plan &&
      accessFeature?.is_active &&
      (!plan.start_date || Date.parse(plan.start_date) <= now) &&
      (!plan.last_date || Date.parse(plan.last_date) >= now)
  );
}

export function useFabrikBillingStatus(tenant?: Tenant, tenantHasFabrikAccess = false) {
  const [billingCheck, setBillingCheck] = useState<{
    tenantId: string;
    status: BillingStatus;
  }>({ tenantId: '', status: 'idle' });

  useEffect(() => {
    if (!tenant || !tenantHasFabrikAccess) {
      setBillingCheck({ tenantId: '', status: 'idle' });
      return;
    }

    const controller = new AbortController();
    const selectedTenantId = tenant.tenant_id;
    setBillingCheck({ tenantId: selectedTenantId, status: 'loading' });
    getFabrikUsage(selectedTenantId, controller.signal)
      .then((plan) => {
        setBillingCheck({
          tenantId: selectedTenantId,
          status: hasActiveFabrikPlan(plan) ? 'allowed' : 'blocked',
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setBillingCheck({ tenantId: selectedTenantId, status: 'error' });
        }
      });

    return () => controller.abort();
  }, [tenant, tenantHasFabrikAccess]);

  return billingCheck.tenantId === tenant?.tenant_id ? billingCheck.status : 'loading';
}
